import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, TextField } from '@tappa/ui';
import { loginSchema, registerSchema } from '@tappa/shared';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Accesso e registrazione.
 *
 * Gli errori di validazione arrivano dagli stessi schemi Zod che usa l'API:
 * il messaggio che l'utente legge e' quello che il server applicherebbe.
 */

type Mode = 'login' | 'register';

export function AccessPage({ mode }: { mode: Mode }): ReactNode {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (auth.user !== null) {
    return <Navigate to={params.get('next') ?? '/oggi'} replace />;
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const schema = mode === 'login' ? loginSchema : registerSchema;
    const candidate =
      mode === 'login' ? { email, password } : { email, password, displayName };
    const parsed = schema.safeParse(candidate);

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && errors[key] === undefined) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setPending(true);
    try {
      if (mode === 'login') {
        await auth.login(email, password);
      } else {
        await auth.register({ email, password, displayName });
      }
      navigate(params.get('next') ?? '/oggi', { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Qualcosa e' + "' andato storto. Riprova.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[var(--content-max-width)] flex-col justify-center px-4 py-10">
      <header className="mb-6 text-center">
        <h1 className="font-display text-[28px] leading-9 text-text">Tappa</h1>
        <p className="mt-1 text-[15px] text-text-muted">
          Il tuo viaggio, giorno per giorno.
        </p>
      </header>

      <Card>
        <h2 className="mb-4 font-display text-[20px] text-text">
          {mode === 'login' ? 'Bentornato' : 'Crea il tuo account'}
        </h2>

        <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
          {mode === 'register' ? (
            <TextField
              label="Come ti chiami"
              autoComplete="name"
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.target.value);
              }}
              {...(fieldErrors['displayName'] !== undefined
                ? { error: fieldErrors['displayName'] }
                : {})}
              required
            />
          ) : null}

          <TextField
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            {...(fieldErrors['email'] !== undefined ? { error: fieldErrors['email'] } : {})}
            required
          />

          <TextField
            label="Password"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            {...(mode === 'register' ? { hint: 'Almeno 10 caratteri.' } : {})}
            {...(fieldErrors['password'] !== undefined ? { error: fieldErrors['password'] } : {})}
            required
          />

          {formError !== null ? (
            <p role="alert" className="text-[14px] text-[var(--budget-blocked)]">
              {formError}
            </p>
          ) : null}

          <Button type="submit" fullWidth size="lg" loading={pending}>
            {mode === 'login' ? 'Accedi' : 'Inizia'}
          </Button>
        </form>
      </Card>

      <p className="mt-5 text-center text-[15px] text-text-muted">
        {mode === 'login' ? (
          <>
            Non hai un account?{' '}
            <Link to="/registrati" className="font-medium text-brand-strong underline">
              Registrati
            </Link>
          </>
        ) : (
          <>
            Hai gia&apos; un account?{' '}
            <Link to="/accedi" className="font-medium text-brand-strong underline">
              Accedi
            </Link>
          </>
        )}
      </p>
    </div>
  );
}
